/**
 * Swiss tournament simulation — ported from the Biadi prototype.
 *
 * Deterministic (seeded RNG) so the whole bracket, every round's pairings,
 * the "me" player's scripted results, byes, eliminations and per-round
 * standings snapshots are stable across renders. This stands in for a real
 * Swiss backend which does not exist yet; a page reads SIM the way it would
 * later read the API.
 */

export interface LeagueConfig {
  name: string;
  format: string;
  lives: number;
  race: number;
  players: number;
  reg: string;
  play: string;
  fee: string;
  est: number;
}

export interface Venue {
  id: string;
  short: string;
  name: string;
  addr: string;
  tables: number;
  cap: number;
  taken: number;
  dist: string;
}

export interface Match {
  round: number;
  winner?: number;
  loser?: number;
  ws?: number | null;
  ls?: number | null;
  venue: string | null;
  table: number | null;
  mine: boolean;
  status: string;
  time?: string;
  bye?: boolean;
  player?: number;
}

export interface Round {
  round: number;
  matches: Match[];
  byeId: number | null;
}

export interface SnapRow {
  id: number;
  wins: number;
  losses: number;
  elimRound: number | null;
}

export interface Snapshot {
  round: number;
  order: SnapRow[];
}

export interface Player {
  id: number;
  name: string;
  ini: string;
  rating: number;
  wins: number;
  losses: number;
  opps: Set<number>;
  elimRound: number | null;
  byeRounds: number[];
}

export const LEAGUE: LeagueConfig = {
  name: "BIADI OPEN SWISS #4",
  format: "Swiss",
  lives: 3,
  race: 5,
  players: 48,
  reg: "01/09 – 10/09/2026",
  play: "12/09 – 20/09/2026",
  fee: "300.000",
  est: 8,
};

export const VENUES: Venue[] = [
  { id: "v1", short: "Q.1", name: "Biadi Arena Quận 1", addr: "12 Lê Lợi, P. Bến Nghé, Q.1, TP.HCM", tables: 8, cap: 20, taken: 12, dist: "2,4 km" },
  { id: "v2", short: "Thủ Đức", name: "Biadi Thủ Đức", addr: "45 Võ Văn Ngân, P. Bình Thọ, TP. Thủ Đức", tables: 6, cap: 14, taken: 14, dist: "11,8 km" },
  { id: "v3", short: "Phú Nhuận", name: "Biadi Phú Nhuận", addr: "78 Phan Xích Long, P.2, Q. Phú Nhuận", tables: 10, cap: 14, taken: 9, dist: "5,1 km" },
];

const NAMES = [
  "Anh Vũ", "Trần Minh", "Lê Trọng Nhân", "Phạm Quốc Bảo", "Nguyễn Hải Đăng", "Đỗ Thành Long", "Vũ Khắc Huy", "Bùi Tấn Phát",
  "Hoàng Gia Kiên", "Lý Nhật Tân", "Trương Công Danh", "Đặng Hữu Lộc", "Ngô Thanh Tùng", "Phan Minh Khoa", "Cao Bá Đạt", "Tạ Quang Vinh",
  "Dương Chí Cường", "Hồ Sỹ Nghĩa", "Lâm Quốc Toàn", "Mai Xuân Trường", "Đinh Bảo Sơn", "Chu Văn Hậu", "Kiều Anh Tú", "Lương Đức Trí",
  "Tô Hoài Nam", "Nguyễn Bá Lực", "Trần Quý Đôn", "Phùng Thế Anh", "Đoàn Minh Quân", "Hà Trung Kiên", "Võ Tuấn Khải", "Lại Đức Phong",
  "Nguyễn Văn Sang", "Trịnh Hoàng Phúc", "Lê Bá Tâm", "Phạm Đăng Khoa", "Bùi Quang Vũ", "Đỗ Nhật Hào", "Vương Chí Thành", "Đào Minh Nghị",
  "Hứa Tấn Lợi", "Lưu Bá Ấn", "Tống Duy Tân", "Quách Đình Huy", "Uông Bảo Ngọc", "Nghiêm Xuân Kỳ", "Thái Bá Đông", "Từ Hải Lâm",
];

/** Scripted results for "me" (player 0) per round. */
const MY_SCRIPT = ["W", "L", "W", "BYE", "L", "L", "W", "W"];

/** State → what the timeline looks like at that moment. */
export interface StateCfg {
  roundsDone: number;
  current: number | null;
  my: string;
  sheet?: string;
  dead?: boolean;
  over?: boolean;
}

export const STATES: Record<string, StateCfg> = {
  waiting_start: { roundsDone: 0, current: null, my: "none" },
  has_match: { roundsDone: 2, current: 3, my: "live" },
  enter_result: { roundsDone: 2, current: 3, my: "live", sheet: "result" },
  waiting_confirm: { roundsDone: 2, current: 3, my: "draft" },
  match_done: { roundsDone: 2, current: 3, my: "confirmed" },
  waiting_pairing: { roundsDone: 3, current: null, my: "none" },
  bye: { roundsDone: 3, current: 4, my: "bye" },
  eliminated: { roundsDone: 6, current: null, my: "none", dead: true },
  finished: { roundsDone: 8, current: null, my: "none", over: true },
};

function rngFactory(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SimResult {
  P: Player[];
  rounds: Round[];
  snaps: Snapshot[];
}

function simulate(): SimResult {
  const rng = rngFactory(20260903);
  const P: Player[] = NAMES.map((n, i) => ({
    id: i,
    name: n,
    ini: n.split(" ").pop()![0],
    rating: 1000 + Math.round((rng() * 2 - 1) * 220),
    wins: 0,
    losses: 0,
    opps: new Set<number>(),
    elimRound: null,
    byeRounds: [],
  }));
  const rounds: Round[] = [];
  const snaps: Snapshot[] = [];
  const alive = (p: Player) => p.losses < LEAGUE.lives;
  const sos = (p: Player) => {
    let s = 0;
    p.opps.forEach((o) => (s += P[o].wins));
    return s;
  };
  const cmp = (a: Player, b: Player) => {
    const aa = alive(a),
      ba = alive(b);
    if (aa !== ba) return aa ? -1 : 1;
    if (aa) {
      if (a.losses !== b.losses) return a.losses - b.losses;
      if (a.wins !== b.wins) return b.wins - a.wins;
      if (sos(b) !== sos(a)) return sos(b) - sos(a);
      return b.rating - a.rating;
    }
    if (a.elimRound !== b.elimRound) return (b.elimRound || 0) - (a.elimRound || 0);
    if (a.wins !== b.wins) return b.wins - a.wins;
    return sos(b) - sos(a);
  };
  const snap = (r: number) =>
    snaps.push({
      round: r,
      order: P.slice()
        .sort(cmp)
        .map((p) => ({ id: p.id, wins: p.wins, losses: p.losses, elimRound: p.elimRound })),
    });
  snap(0);

  for (let r = 1; r <= LEAGUE.est; r++) {
    const act = P.filter(alive);
    if (act.length <= 1) break;
    const groups: Record<number, Player[]> = {};
    for (let L = 0; L < LEAGUE.lives; L++) groups[L] = [];
    act.forEach((p) => groups[p.losses].push(p));
    const inner = (g: Player[]) => g.sort((a, b) => b.wins - a.wins || sos(b) - sos(a) || b.rating - a.rating);
    const pairs: Player[][] = [];
    let floater: Player | null = null;
    for (let L = 0; L < LEAGUE.lives; L++) {
      let g = inner(groups[L].slice());
      if (floater) {
        g = [floater, ...g];
        floater = null;
      }
      while (g.length >= 2) {
        const a = g.shift()!;
        let j = 0;
        while (j < g.length && a.opps.has(g[j].id)) j++;
        if (j >= g.length) j = 0;
        pairs.push([a, g.splice(j, 1)[0]]);
      }
      if (g.length) floater = g[0];
    }
    // Keep "me" on script for BYE rounds.
    const script = MY_SCRIPT[r - 1];
    const me = P[0];
    if (alive(me)) {
      if (script === "BYE" && floater !== me) {
        const idx = pairs.findIndex((pr) => pr[0] === me || pr[1] === me);
        if (idx > -1) {
          const pr = pairs[idx];
          const other = pr[0] === me ? pr[1] : pr[0];
          if (floater) {
            pairs[idx] = [other, floater];
          } else {
            pairs.splice(idx, 1);
          }
          floater = me;
        }
      } else if (script !== "BYE" && floater === me) {
        const pr = pairs[pairs.length - 1];
        if (pr) {
          floater = pr[1];
          pairs[pairs.length - 1] = [pr[0], me];
        }
      }
    }
    const matches: Match[] = [];
    const tblIdx: Record<string, number> = { v1: 0, v2: 0, v3: 0 };
    pairs.forEach((pr) => {
      const [a, b] = pr;
      const isMine = a === P[0] || b === P[0];
      let A: Player, B: Player;
      if (isMine && alive(P[0])) {
        const s = MY_SCRIPT[r - 1];
        if (s === "W") {
          A = P[0];
          B = a === P[0] ? b : a;
        } else {
          B = P[0];
          A = a === P[0] ? b : a;
        }
      } else {
        const d = (a.rating - b.rating) / 180,
          pa = 1 / (1 + Math.exp(-d));
        if (rng() < pa) {
          A = a;
          B = b;
        } else {
          A = b;
          B = a;
        }
      }
      const ls = Math.floor(rng() * LEAGUE.race * 0.85);
      A.wins++;
      B.losses++;
      A.opps.add(B.id);
      B.opps.add(A.id);
      if (B.losses >= LEAGUE.lives) B.elimRound = r;
      const v = VENUES[Math.floor(rng() * 3)];
      tblIdx[v.id]++;
      matches.push({
        round: r,
        winner: A.id,
        loser: B.id,
        ws: LEAGUE.race,
        ls,
        venue: v.id,
        table: tblIdx[v.id],
        mine: isMine,
        status: "CONFIRMED",
        time: `${17 + Math.floor(rng() * 4)}:${rng() < 0.5 ? "00" : "30"}`,
      });
    });
    let byeId: number | null = null;
    if (floater) {
      floater.wins++;
      floater.byeRounds.push(r);
      byeId = floater.id;
      matches.push({ round: r, bye: true, player: floater.id, mine: floater.id === 0, status: "BYE", venue: null, table: null });
    }
    rounds.push({ round: r, matches, byeId });
    snap(r);
  }
  return { P, rounds, snaps };
}

export const SIM: SimResult = simulate();

export const pName = (id: number) => SIM.P[id].name;
export const pIni = (id: number) => SIM.P[id].ini;
export const venueById = (id: string | null) => VENUES.find((v) => v.id === id);

export interface Ctx {
  cfg: StateCfg;
  w: number;
  l: number;
  lives: number;
  cur: Match | null;
  rank: number;
  snapIdx: number;
  aliveCount: number;
  byeCount: number;
  curRound: number;
  dead: boolean;
  over: boolean;
}

/** Derived, per-state view context for "me" — mirrors prototype ctx(). */
export function buildCtx(state: string): Ctx {
  const cfg = STATES[state];
  const rounds = SIM.rounds.slice(0, cfg.roundsDone);
  let w = 0,
    l = 0,
    byeCount = 0;
  rounds.forEach((r) =>
    r.matches.forEach((m) => {
      if (m.bye && m.player === 0) {
        w++;
        byeCount++;
      } else if (!m.bye && (m.winner === 0 || m.loser === 0)) {
        m.winner === 0 ? w++ : l++;
      }
    })
  );
  let cur: Match | null = null;
  if (cfg.current) {
    const rr = SIM.rounds[cfg.current - 1];
    if (rr) {
      const mm = rr.matches.find((m) => m.mine);
      if (mm) {
        cur = JSON.parse(JSON.stringify(mm));
        if (cfg.my === "live") cur!.status = "LIVE";
        else if (cfg.my === "draft") cur!.status = "DRAFT";
        else if (cfg.my === "confirmed") cur!.status = "CONFIRMED";
        else if (cfg.my === "bye") cur!.status = "BYE";
      }
    }
  }
  const lives = LEAGUE.lives - l;
  const snapIdx = Math.min(cfg.roundsDone, SIM.snaps.length - 1);
  const snap = SIM.snaps[snapIdx];
  const rank = snap.order.findIndex((o) => o.id === 0) + 1;
  const aliveCount = snap.order.filter((o) => o.elimRound === null).length;
  return {
    cfg,
    w,
    l,
    lives,
    cur,
    rank,
    snapIdx,
    aliveCount,
    byeCount,
    curRound: cfg.current || cfg.roundsDone,
    dead: !!cfg.dead,
    over: !!cfg.over,
  };
}

/** Rounds visible in the "Thi đấu" tab: done rounds + current (with mixed live statuses). */
export function visibleRounds(state: string): Round[] {
  const cfg = STATES[state];
  const cur = buildCtx(state);
  const out: Round[] = [];
  for (let i = 0; i < cfg.roundsDone; i++) {
    const r = SIM.rounds[i];
    if (!r) continue;
    out.push({ round: r.round, byeId: r.byeId, matches: r.matches.map((m) => ({ ...m })) });
  }
  if (cfg.current) {
    const r = SIM.rounds[cfg.current - 1];
    if (r) {
      const rng = rngFactory(777 + cfg.current);
      const ms = r.matches.map((m) => {
        const mm = { ...m };
        if (mm.mine) {
          mm.status = cur.cur ? cur.cur.status : "LIVE";
        } else {
          const x = rng();
          mm.status = x < 0.42 ? "CONFIRMED" : x < 0.62 ? "DRAFT" : "LIVE";
        }
        if (mm.status === "LIVE") {
          mm.ws = null;
          mm.ls = null;
        }
        return mm;
      });
      out.push({ round: r.round, byeId: r.byeId, matches: ms, ...( { live: true } as any) });
    }
  }
  return out.reverse();
}
